import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Megaphone, CreditCard, FileText, LogOut, Loader2, Download, Bell } from "lucide-react";

export default function Portal() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [products, setProducts] = useState([]);
  const [paying, setPaying] = useState("");

  useEffect(() => {
    if (user === false) nav("/login");
    if (user && user.role === "admin") nav("/admin");
  }, [user, nav]);

  useEffect(() => {
    if (user && user.role === "parent") {
      api.get("/announcements").then(({ data }) => setAnnouncements(data)).catch(() => {});
      api.get("/payments/products").then(({ data }) => setProducts(data)).catch(() => {});
    }
  }, [user]);

  const pay = async (lookup_key) => {
    setPaying(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Payment error");
      setPaying("");
    }
  };

  if (!user || user === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  const resources = [
    { name: "Parent Handbook 2026", size: "PDF · 2.1 MB" },
    { name: "Enrollment Forms Packet", size: "PDF · 850 KB" },
    { name: "Health & Immunization Form", size: "PDF · 420 KB" },
    { name: "Annual School Calendar", size: "PDF · 600 KB" },
    { name: "Meal & Allergy Policy", size: "PDF · 300 KB" },
  ];

  return (
    <div className="min-h-screen bg-cream pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-coral font-semibold tracking-widest uppercase text-xs">{t.portal.welcome}</div>
            <h1 className="font-heading text-3xl font-bold text-navy">{user.name}</h1>
            {user.child_name && <p className="text-navy/60 mt-1">Parent of {user.child_name}</p>}
          </div>
          <Button variant="outline" onClick={() => { logout(); nav("/"); }} className="rounded-full gap-2" data-testid="portal-logout-btn"><LogOut className="h-4 w-4" /> {t.portal.logout}</Button>
        </div>

        <Tabs defaultValue="announcements">
          <TabsList className="bg-white rounded-full p-1 h-auto flex-wrap">
            <TabsTrigger value="announcements" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white gap-1.5" data-testid="portal-tab-announcements"><Megaphone className="h-4 w-4" /> {t.portal.announcements}</TabsTrigger>
            <TabsTrigger value="payments" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white gap-1.5" data-testid="portal-tab-payments"><CreditCard className="h-4 w-4" /> {t.portal.payments}</TabsTrigger>
            <TabsTrigger value="resources" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white gap-1.5" data-testid="portal-tab-resources"><FileText className="h-4 w-4" /> {t.portal.resources}</TabsTrigger>
          </TabsList>

          <TabsContent value="announcements" className="mt-6 space-y-4">
            {announcements.length === 0 && <p className="text-navy/50">No announcements yet.</p>}
            {announcements.map((a) => (
              <div key={a.id} className="bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid={`announcement-${a.id}`}>
                <div className="flex items-center gap-2 text-coral"><Bell className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wider">{new Date(a.created_at).toLocaleDateString()}</span></div>
                <h3 className="font-heading text-lg font-bold text-navy mt-2">{a.title}</h3>
                <p className="text-navy/70 mt-1 leading-relaxed">{a.body}</p>
                <div className="text-xs text-navy/40 mt-3">— {a.author}</div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="payments" className="mt-6">
            <div className="grid sm:grid-cols-2 gap-4">
              {products.map((p) => (
                <div key={p.lookup_key} className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 flex flex-col" data-testid={`payment-${p.lookup_key}`}>
                  <h3 className="font-heading text-lg font-bold text-navy">{p.name}</h3>
                  <p className="text-sm text-navy/60 mt-1 flex-1">{p.description}</p>
                  <div className="font-heading text-2xl font-extrabold text-navy mt-3">${p.amount.toFixed(0)}<span className="text-sm font-normal text-navy/50">{p.recurring ? "/mo" : ""}</span></div>
                  <Button onClick={() => pay(p.lookup_key)} disabled={paying === p.lookup_key} className="mt-4 rounded-full bg-coral hover:bg-coral-dark text-white" data-testid={`pay-btn-${p.lookup_key}`}>
                    {paying === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : t.portal.pay}
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-xs text-navy/40 mt-4">Secure payments powered by Stripe. Test card: 4242 4242 4242 4242.</p>
          </TabsContent>

          <TabsContent value="resources" className="mt-6 space-y-3">
            {resources.map((r, i) => (
              <div key={i} className="bg-white rounded-2xl p-4 shadow-sm border border-black/5 flex items-center justify-between" data-testid={`resource-${i}`}>
                <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-xl bg-navy/5 flex items-center justify-center"><FileText className="h-5 w-5 text-navy" /></div><div><div className="font-semibold text-navy">{r.name}</div><div className="text-xs text-navy/40">{r.size}</div></div></div>
                <Button variant="ghost" size="sm" onClick={() => toast.info("Document downloads will be enabled once uploaded by admin.")} className="rounded-full gap-1.5 text-coral"><Download className="h-4 w-4" /> Download</Button>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
